<template>
  <teleport to="body">
    <transition name="tour-fade">
      <div v-if="visible" class="tour-root">
        <!-- Dimming overlay -->
        <div class="tour-overlay"></div>

        <!-- Highlight ring on target -->
        <div
          v-if="targetRect"
          class="tour-highlight"
          :style="highlightStyle"
        ></div>

        <!-- Tooltip card -->
        <div
          v-if="targetRect"
          class="tour-tooltip"
          :style="tooltipStyle"
        >
          <!-- Arrow -->
          <div class="tour-arrow" :class="arrowClass" :style="arrowStyle"></div>

          <!-- Content -->
          <div class="tour-tooltip-header">
            <span class="tour-step-badge">{{ currentStep + 1 }}/{{ steps.length }}</span>
            <button class="tour-close-btn" @click="finish" :title="$t('Skip Tour')">&times;</button>
          </div>
          <div class="tour-tooltip-body">
            <h3 class="tour-tooltip-keyword">{{ steps[currentStep].keyword }}</h3>
            <p class="tour-tooltip-desc">{{ steps[currentStep].desc }}</p>
          </div>
          <div class="tour-tooltip-footer">
            <span class="tour-dots">
              <span
                v-for="(s, i) in steps" :key="i"
                class="tour-dot"
                :class="{ active: i === currentStep, done: i < currentStep }"
              ></span>
            </span>
            <div class="tour-actions">
              <el-button v-if="currentStep > 0" text size="small" @click="prev">{{ $t('Back') }}</el-button>
              <el-button type="primary" size="small" @click="currentStep < steps.length - 1 ? next() : finish()">
                {{ currentStep < steps.length - 1 ? $t('Next') : $t('Get Started') }}
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { ElButton } from 'element-plus';

export default {
  name: 'OnboardingTour',
  components: { ElButton },
  props: {
    visible: { type: Boolean, default: false }
  },
  emits: ['close'],
  data() {
    return {
      currentStep: 0,
      targetRect: null,
      tooltipPlacement: 'bottom',
      resizeObs: null,
      scrollCb: null
    };
  },
  computed: {
    steps() {
      return [
        { target: 'input',   keyword: this.$t('Dataset'),     title: this.$t('onboarding_input_title'),   desc: this.$t('onboarding_input_desc') },
        { target: 'size',    keyword: this.$t('Dimensions'),  title: this.$t('onboarding_size_title'),    desc: this.$t('onboarding_size_desc') },
        { target: 'font',    keyword: this.$t('Font'),        title: this.$t('onboarding_font_title'),    desc: this.$t('onboarding_font_desc') },
        { target: 'colors',  keyword: this.$t('Word Colors'), title: this.$t('onboarding_color_title'),   desc: this.$t('onboarding_color_desc') },
        { target: 'background', keyword: this.$t('Background Image'), title: this.$t('onboarding_bg_title'), desc: this.$t('onboarding_bg_desc') },
        { target: 'shape',   keyword: this.$t('Shape Mask'),  title: this.$t('onboarding_shape_title'),   desc: this.$t('onboarding_shape_desc') },
        { target: 'export',  keyword: this.$t('Export'),      title: this.$t('onboarding_export_title'),  desc: this.$t('onboarding_export_desc') }
      ];
    },
    highlightStyle() {
      if (!this.targetRect) return {};
      return {
        top: `${this.targetRect.top - 4}px`,
        left: `${this.targetRect.left - 4}px`,
        width: `${this.targetRect.width + 8}px`,
        height: `${this.targetRect.height + 8}px`
      };
    },
    arrowClass() {
      return `tour-arrow--${this.tooltipPlacement}`;
    },
    arrowStyle() {
      // Position arrow opposite to placement direction
      const r = this.targetRect;
      if (!r) return {};
      if (this.tooltipPlacement === 'bottom') return { top: '-6px', left: '50%', marginLeft: '-6px' };
      if (this.tooltipPlacement === 'top')    return { bottom: '-6px', left: '50%', marginLeft: '-6px' };
      if (this.tooltipPlacement === 'right')  return { left: '-6px', top: '50%', marginTop: '-6px' };
      if (this.tooltipPlacement === 'left')   return { right: '-6px', top: '50%', marginTop: '-6px' };
      return {};
    },
    tooltipStyle() {
      const r = this.targetRect;
      if (!r) return {};
      const gap = 12 + 8; // arrow + space
      const cardW = 340;
      const vw = window.innerWidth;

      let left, top;
      if (this.tooltipPlacement === 'bottom') {
        top = r.bottom + gap;
        left = Math.max(16, Math.min(vw - cardW - 16, r.left + r.width / 2 - cardW / 2));
      } else if (this.tooltipPlacement === 'top') {
        top = r.top - gap;
        left = Math.max(16, Math.min(vw - cardW - 16, r.left + r.width / 2 - cardW / 2));
        return { top: `${top}px`, left: `${left}px`, transform: 'translateY(-100%)', maxWidth: `${cardW}px` };
      } else if (this.tooltipPlacement === 'right') {
        left = r.right + gap;
        top = r.top + r.height / 2;
        return { top: `${top}px`, left: `${left}px`, transform: 'translateY(-50%)', maxWidth: `${cardW}px` };
      } else {
        left = r.left - cardW - gap;
        top = r.top + r.height / 2;
        return { top: `${top}px`, left: `${left}px`, transform: 'translateY(-50%) translateX(-100%)', maxWidth: `${cardW}px` };
      }
      return { top: `${top}px`, left: `${left}px`, maxWidth: `${cardW}px` };
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.currentStep = 0;
        this.$nextTick(() => {
          this.updateTarget();
          this.startTracking();
        });
      } else {
        this.stopTracking();
        this.targetRect = null;
      }
    }
  },
  beforeUnmount() {
    this.stopTracking();
  },
  methods: {
    getTargetEl() {
      const selector = `[data-tour="${this.steps[this.currentStep].target}"]`;
      return document.querySelector(selector);
    },
    updateTarget() {
      const el = this.getTargetEl();
      if (el) {
        const r = el.getBoundingClientRect();
        this.targetRect = { top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom, right: r.right };
        // Auto-placement: prefer bottom, use top if not enough space
        const spaceBelow = window.innerHeight - r.bottom;
        const spaceAbove = r.top;
        if (spaceBelow < 220 && spaceAbove > 220) {
          this.tooltipPlacement = 'top';
        } else if (r.left + r.width / 2 > window.innerWidth * 0.7) {
          this.tooltipPlacement = 'left';
        } else if (r.left + r.width / 2 < window.innerWidth * 0.3) {
          this.tooltipPlacement = 'right';
        } else {
          this.tooltipPlacement = 'bottom';
        }
        // Scroll target into view if needed
        if (r.top < 60 || r.bottom > window.innerHeight - 20) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => this.updateTarget(), 400);
        }
      } else {
        this.targetRect = null;
      }
    },
    startTracking() {
      this.resizeObs = new ResizeObserver(() => this.updateTarget());
      this.scrollCb = () => this.updateTarget();
      window.addEventListener('scroll', this.scrollCb, true);
      this.resizeObs.observe(document.body);
    },
    stopTracking() {
      if (this.resizeObs) { this.resizeObs.disconnect(); this.resizeObs = null; }
      if (this.scrollCb) { window.removeEventListener('scroll', this.scrollCb, true); this.scrollCb = null; }
    },
    next() {
      if (this.currentStep < this.steps.length - 1) {
        this.currentStep++;
        this.$nextTick(() => this.updateTarget());
      }
    },
    prev() {
      if (this.currentStep > 0) {
        this.currentStep--;
        this.$nextTick(() => this.updateTarget());
      }
    },
    finish() {
      this.currentStep = 0;
      this.targetRect = null;
      this.stopTracking();
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
/* ═══ Root ═══ */
.tour-root {
  position: fixed;
  inset: 0;
  z-index: 10000;
  pointer-events: none;
}

/* ═══ Overlay ═══ */
.tour-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  pointer-events: auto;
}

/* ═══ Highlight ring ═══ */
.tour-highlight {
  position: fixed;
  z-index: 10001;
  border-radius: 10px;
  border: 3px solid #4f46e5;
  box-shadow:
    0 0 0 9999px rgba(0, 0, 0, 0.55),
    0 0 24px rgba(79, 70, 229, 0.45),
    inset 0 0 24px rgba(79, 70, 229, 0.1);
  pointer-events: none;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  animation: tour-pulse 2s ease-in-out infinite;
}
@keyframes tour-pulse {
  0%, 100% { box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55), 0 0 24px rgba(79, 70, 229, 0.45), inset 0 0 24px rgba(79, 70, 229, 0.1); }
  50%      { box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55), 0 0 40px rgba(79, 70, 229, 0.7),  inset 0 0 36px rgba(79, 70, 229, 0.2); }
}

/* ═══ Tooltip ═══ */
.tour-tooltip {
  position: fixed;
  z-index: 10002;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.25), 0 2px 10px rgba(0,0,0,0.1);
  width: 100%;
  pointer-events: auto;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;
  border: 1px solid rgba(79,70,229,0.15);
}

/* Arrow */
.tour-arrow {
  position: absolute;
  width: 14px;
  height: 14px;
  background: #fff;
  transform: rotate(45deg);
  border: 1px solid rgba(79,70,229,0.15);
  border-top: none;
  border-left: none;
}

/* Header */
.tour-tooltip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px 0;
}
.tour-step-badge {
  font-size: 11px;
  font-weight: 600;
  color: #4f46e5;
  background: #eef2ff;
  padding: 2px 8px;
  border-radius: 9999px;
}
.tour-close-btn {
  width: 24px;
  height: 24px;
  border: none;
  background: #f3f4f6;
  color: #9ca3af;
  border-radius: 50%;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tour-close-btn:hover { background: #e5e7eb; color: #4b5563; }

/* Body */
.tour-tooltip-body { padding: 12px 16px; }
.tour-tooltip-keyword { font-size: 22px; font-weight: 800; color: #4f46e5; margin: 0 0 8px; line-height: 1.2; letter-spacing: -0.01em; }
.tour-tooltip-desc { font-size: 13px; color: #4b5563; line-height: 1.5; margin: 0; }

/* Footer */
.tour-tooltip-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px 14px;
  border-top: 1px solid #f3f4f6;
}
.tour-dots { display: flex; gap: 5px; }
.tour-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #e5e7eb;
  transition: all 0.2s;
}
.tour-dot.active { background: #4f46e5; transform: scale(1.4); }
.tour-dot.done { background: #818cf8; }
.tour-actions { display: flex; gap: 6px; align-items: center; }

/* ═══ Transition ═══ */
.tour-fade-enter-active { transition: opacity 0.25s ease; }
.tour-fade-leave-active { transition: opacity 0.2s ease; }
.tour-fade-enter-from, .tour-fade-leave-to { opacity: 0; }
</style>
